import { Box, CircularProgress, FormLabel, Stack, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Option } from './FormSelect'

type Props = {
  name: string
  label: string
  options: Option[]
  defaultValue: Option[]
  isLoading: boolean
  disabled?: boolean
  placeholder?: string
}

const isOptionEqualToValue = (option: Option, value: Option) => option.value === value.value
const getOptionLabel = (option: Option) => option.label

const FormMultipleSelect = (props: Props) => {
  const { options, isLoading, label, name, defaultValue, disabled = false, placeholder } = props

  const {
    control,
    formState: { errors },
  } = useFormContext()

  // Custom option rendering (label + greyed description) is opt-in: only when some
  // option provides a description. Otherwise the default MUI rendering is kept so
  // other selects in the project are not affected.
  const renderOption = options.some((option) => option.description)
    ? (optionProps: React.HTMLAttributes<HTMLLIElement>, option: Option) => (
        <Box component="li" {...optionProps} key={option.value.toString()}>
          <Typography variant="body2" component="span">
            {option.label}
          </Typography>
          {option.description ? (
            <Typography variant="body2" component="span" color="text.secondary">
              {` — ${option.description}`}
            </Typography>
          ) : null}
        </Box>
      )
    : undefined

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <Stack spacing={0.5}>
            <FormLabel>{label}</FormLabel>
            <Autocomplete
              multiple
              isOptionEqualToValue={isOptionEqualToValue}
              getOptionLabel={(a) => getOptionLabel(a).toString()}
              options={options}
              loading={isLoading}
              disabled={disabled}
              value={field.value}
              defaultValue={defaultValue}
              onChange={(e, value) => field.onChange(value)}
              openOnFocus={true}
              renderOption={renderOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  name={name}
                  placeholder={placeholder}
                  error={!!errors[name]}
                  helperText={(errors[name] ? errors[name]?.message : '') as string}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        )
      }}
    />
  )
}

export default React.memo(FormMultipleSelect)
